import { connect } from "react-redux";
import SalaryDetails from "./SalaryDetails";
import { useMemo, useState, useEffect } from "react";
import { NotificationManager } from "react-notifications";
import cleanDeep from "clean-deep";
import { Actions as EMPLOYEEACTIONS } from "@/redux/actions/employeeActions";
import { format, subYears, addYears, parse } from "date-fns";
import { mapSalariesForDropdown } from "@/helpers/Remappers";
import BambooPushChanges from "@/integrations/bamboo-hr/BambooPushChanges";
import DatePicker from "react-datepicker";
import { DateInputFormat, handleRawDate } from "@/common/DateComponents";
import Select from "react-select";
import { employmentTypeOptions } from "@/components/company-settings/config/employmentTypeOptions";

const defaultWorking = {
	"501": { days: ["2", "3", "4", "5", "6"], hours: 40 },
	"502": { days: ["2", "3", "4", "5", "6"], hours: 20 },
};

const EmployeeSalary = ({
	forNewEmployee,
	occupation,
	prevPage,
	nextPage,
	forReview,
	forReviewAgreement,
	notification,
	confirmChange,
	confirmModal,
	salary,
	salaries,
	employeeStartDate,
	payScheduleOptions,
	isLast,
	gradeApplied,
	agreementId,
	disabled,
	permitted,
	setSalary,
	salaryPayslips,
	deleteSalaryModal,
	employeeName,
	isTerminated,
}) => {
	const [data, setData] = useState({});

	const updateField = (field, value) => {
		const newData = {
			...data,
			[field]: value,
		};

		setData(newData);
	};

	const onSubmit = () => {
		data.id = salary?.id;
		data.occupation = salary?.occupation || occupation;
		if (salary?.grade) data.grade = salary.grade;

		if (
			(payScheduleOptions?.length > 1 && !data.paySchedule) ||
			!data.salaryAmount ||
			!data.salaryPeriod ||
			!data.employmentType
		) {
			NotificationManager.error("Kindly Ensure All Required Fields* Are Filled!", "Saving Salary");
			return;
		}

		//clean up
		data.allowances = data.allowances?.filter((a) => a.type && a.amount);
		data.overtime = data.overtime?.filter((o) => !!o);
		if (data.casual) {
			delete data.weeklyHours;
			delete data.workingDays;
		} else delete data.previousHours;

		confirmModal({
			input: cleanDeep(data, { emptyStrings: false, emptyArrays: false, emptyObjects: false }),
			status: true,
			formType: "Salary",
			previousDate: salary?.start,
			isLast,
		});

		if (forReview) {
			const newDate = data?.start != forReview.originalDate;
			confirmChange({
				date: newDate ? data?.start : forReview.originalDate,
				replace: !newDate,
				agreement: forReviewAgreement,
				notification,
			});
		}

		if (forNewEmployee) {
			confirmChange({
				date: employeeStartDate,
				replace: salaries?.length > 0,
				onSuccess: nextPage,
			});
		}
	};

	const { salaryID, hasSalary } = useMemo(() => {
		return {
			salaryID: salary?.id ? String(salary?.id) : undefined,
			hasSalary: Object.keys(cleanDeep(salary))?.length > 1,
		};
	}, [salary?.id]);

	const selectedSalary = useMemo(() => {
		return salaries?.find((s) => s.value == salaryID) || null;
	}, [salaries, salaryID, occupation]);

	useEffect(() => {
		if (!hasSalary) {
			setData({
				salaryAmount: 0,
				paySchedule: payScheduleOptions?.[0]?.value,
				previousHours: null,
				salaryPeriod: { label: "Hour", value: 105 },
				net: false,
				suspendBonus: false,
				isManagerial: false,
				casual: false,
				employmentType: employmentTypeOptions[0].value,
				workingDays: ["2", "3", "4", "5", "6"],
				weeklyHours: 40,
				allowances: [],
				grade: null,
				start: null,
				overtime: [],
			});
		} else {
			if (salary.workingDays) {
				// clean up numerics and duplicates
				salary.workingDays = [...new Set(salary.workingDays.map((s) => String(s)))];
			}

			setData({
				salaryAmount: parseFloat(salary.salaryAmount) || 0,
				weeklyHours: salary.weeklyHours
					? parseFloat(salary.weeklyHours) || 0
					: defaultWorking[salary.employmentType]?.hours,
				previousHours: parseFloat(salary.previousHours) || 0,
				net: typeof salary.net == "string" ? salary.net == "yes" : salary.net,
				workingDays: salary && !forReview ? salary.workingDays : defaultWorking[salary.employmentType]?.days,
				// ensures if payschedules are removed or filtered the default one is used even if there is only 1 option.
				paySchedule: payScheduleOptions.length === 1 ? payScheduleOptions?.[0]?.value : salary.paySchedule,
				salaryPeriod: salary.salaryPeriod || salary.payPeriodFrequency,
				employmentType: salary.employmentType,
				overtime: salary.overtime || [],
				allowances: salary.allowances || [],
				isManagerial:
					typeof salary.isManagerial == "string" ? salary.isManagerial == "yes" : salary.isManagerial,
				casual: typeof salary.casual == "string" ? salary.casual == "yes" : salary.casual,
				suspendBonus:
					typeof salary.suspendBonus == "string" ? salary.suspendBonus == "yes" : salary.suspendBonus,
				start: forReview ? salary.start : undefined,
				grade: forReview ? undefined : salary.grade,
			});
		}
	}, [salaryID, hasSalary, occupation]);

	useEffect(() => {
		if (!hasSalary || !salary?.grade) return;
		setData({ ...salary });
	}, [salary?.grade]);

	return (
		<div className="content">
			{!forNewEmployee && !(forReview?.id && salary?.effectivePensionDate) ? (
				<div className="col-xs-12 form-section">
					<div className="col-xs-1 pull-left">
						<h1 className="block-title">Salary</h1>
					</div>
					<div className="col-xs-11 bottom-4 pull-right">
						{forReview || forNewEmployee ? (
							<div
								className="form-material"
								style={{ paddingLeft: 0 }}
							>
								<label htmlFor="material-text">Effective Date</label>
								<DatePicker
									autoComplete="off"
									dateFormat="dd MMM yyyy"
									minDate={subYears(new Date(), 100)}
									maxDate={addYears(new Date(), 100)}
									className="form-control form-input"
									onChange={(date) => {
										updateField("start", date ? format(date, "yyyy-MM-dd") : undefined);
									}}
									onChangeRaw={(e) => {
										handleRawDate(e.target.value, (date) => {
											updateField("start", date ? format(date, "yyyy-MM-dd") : undefined);
										});
									}}
									strictParsing
									selected={data.start && parse(data.start, "yyyy-MM-dd", new Date())}
									customInput={<DateInputFormat />}
								/>
							</div>
						) : (
							<>
								<Select
									options={salaries}
									value={selectedSalary}
									onChange={(newSalary) => {
										setSalary(newSalary.value);
									}}
								/>
								{permitted &&
									salaryPayslips?.length == 0 &&
									salaries?.length > 1 &&
									salary?.id == salaries[salaries?.length - 1].value && (
										<>
											<button
												className="btn btn-danger pull-right"
												type="submit"
												style={{
													position: "absolute",
													top: 6,
													right: 50,
													fontSize: 12,
													fontWeight: "normal",
													padding: "4px 8px",
												}}
												onClick={(e) => {
													e.stopPropagation();
													e.preventDefault();
													deleteSalaryModal();
												}}
											>
												Delete
											</button>
										</>
									)}
							</>
						)}
					</div>
				</div>
			) : null}
			<SalaryDetails
				location="Employee"
				data={data}
				setData={setData}
				disabled={disabled || !permitted || !isLast}
				forNewEmployee={forNewEmployee}
				gradeApplied={gradeApplied}
				employeeName={employeeName}
				isTerminated={isTerminated}
				forReview={forReview}
				salary={salary}
			/>
			{!forNewEmployee && isLast && (
				<div>
					<button
						className="btn btn-success pull-right"
						type="submit"
						style={{ marginBottom: "30px", marginLeft: "0.5rem" }}
						onClick={onSubmit}
					>
						Update Salary Details
					</button>
					{!forReview && (
						<BambooPushChanges
							agreement={agreementId}
							section={"salary"}
							disabled={disabled || !permitted || !isLast}
						/>
					)}
				</div>
			)}
			{forNewEmployee && (
				<div style={{ marginTop: 20 }}>
					{prevPage && (
						<button
							style={{ marginTop: 10, marginBottom: 10 }}
							className="btn btn-default"
							onClick={prevPage}
						>
							Back
						</button>
					)}
					<button
						className="btn btn-success right"
						type="submit"
						style={{ marginTop: 10, marginBottom: 10 }}
						onClick={onSubmit}
					>
						{gradeApplied ? "Confirm Salary" : "Save & Continue"}
					</button>
				</div>
			)}
		</div>
	);
};

const mapStateToProps = (state, ownProps) => {
	const { salaries: salaryList, selectedSalary, agreementInfo } = state.employee;
	const gradeSalary = state.grades.apply?.salary;
	const gradeApplied = !!state.grades?.apply?.id;
	const { forNewEmployee, forReview } = ownProps;

	const salaries = salaryList?.filter((s) => (ownProps.occupation ? s.occupation === ownProps.occupation : true));

	let salary = {};

	if (gradeApplied) {
		salary = {
			...gradeSalary,
			grade: state.grades.apply.id,
		};
	} else {
		const currentSalary = forReview
			? { ...forReview, id: forReview?.id }
			: selectedSalary
				? salaries?.find((s) => s.id == selectedSalary)
				: salaries?.[salaries.length - 1];

		const employeeSalary = {
			...currentSalary,
			allowances: currentSalary?.allowances?.map((allowance) => ({
				...allowance,
				type: allowance.allowanceType || allowance.type,
			})),
			overtime: currentSalary?.overtime
				?.filter((overtime) => !!overtime)
				?.map((overtime) => (overtime?.overtimeType ? overtime?.overtimeType : overtime)),
		};
		salary = employeeSalary;
	}
	const salaryHasGrade = !!salary?.grade;

	// const terminationDate = !lastSalary || lastSalary.start == lastSalary.end ? null : lastSalary.end

	const isTerminated = agreementInfo?.terminationDate
		? agreementInfo?.terminationDate < format(new Date(), "yyyy-MM-dd")
		: false;
	const isLast = salaries ? salary?.id == salaries?.[salaries?.length - 1]?.id : false;

	const payslips = state?.payslipHistory?.payslips;

	const salaryPayslips =
		payslips?.filter(
			(payslip) => payslip.ready && payslip.basicSalaryBreakdown?.find((breakdown) => breakdown.id == salary?.id),
		) || [];

	const isCis = state.employee?.agreementInfo?.type === "cis";
	return {
		salaryConfirmModal: !!state.employee?.confirmModal,
		salaryPayslips,
		salaries: mapSalariesForDropdown(salaries, state.company?.settings?.currency),
		agreementId: agreementInfo?.id,
		salary,
		payScheduleOptions: state.company?.settings?.paySchedules
			?.filter((ps) => !ps.temporary && (isCis ? ps.type === "cis" : ps.type !== "cis"))
			?.map((ps) => ({
				label: ps.name,
				value: ps.id,
				payPeriods: ps.payFrequency,
			})),
		employeeName: agreementInfo?.employeeDetails
			? `${agreementInfo?.employeeDetails?.givenName} ${agreementInfo?.employeeDetails?.familyName}`
			: "This employee",
		employeeStartDate: agreementInfo?.engagementDate,
		// eventually do not disable if not last but allow some restricted edits
		disabled: !!isTerminated,
		formsDownloading: state.fssDownloading,
		isLast: forNewEmployee || forReview ? true : isLast,
		permitted: state?.permissions?.my?.permissions?.people != "Read",
		isTerminated,
		gradeApplied: salaryHasGrade || gradeApplied,
	};
};

const mapDispatchToProps = (dispatch) => ({
	confirmChange: (params) => dispatch(EMPLOYEEACTIONS.confirmChange(params)),
	setSalary: (salaryId) => dispatch(EMPLOYEEACTIONS.setSalary(salaryId)),
	deleteSalaryModal: () => dispatch(EMPLOYEEACTIONS.deleteSalaryModal(true)),
	confirmModal: (options) => dispatch(EMPLOYEEACTIONS.confirmModal(options)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EmployeeSalary);
